import { sharedEnvironment } from "../config/environmentService";
import { coachToMaiaFallbackVoiceFolder, resolveCoachFromPreferences } from "../domain/coach";
import { DEFAULT_LOCALE, WebsiteLocaleMapping, type AppLocale } from "../infra/locales";

const DEFAULT_MEDIA_STREAMING_DISTRIBUTION_URL = sharedEnvironment.defaultMediaStreamingDistributionUrl;
const MEDIA_STREAMING_DISTRIBUTION_URL = sharedEnvironment.mediaStreamingDistributionUrl;

export const MAIA_VOICE_PLACEHOLDER = "{voice}";
export const MAIA_LANG_PLACEHOLDER = "{lang}";
export const DEFAULT_MAIA_VOICE = coachToMaiaFallbackVoiceFolder(
  resolveCoachFromPreferences(undefined, undefined),
);

const MAIA_ECHO_FALLBACKS_PATH = /(\/maia-echo-fallbacks\/)([^/]+)(\/)([^/]+)(\/)/;

export type MediaStreamingUrlOptions = {
  voice?: string;
  locale?: string;
  coachSound?: string;
  coachStyle?: string;
};

function normalizeOptions(optionsOrVoice?: string | MediaStreamingUrlOptions): MediaStreamingUrlOptions {
  if (typeof optionsOrVoice === "string") {
    return { voice: optionsOrVoice };
  }
  return optionsOrVoice ?? {};
}

function resolveMaiaFallbackLanguage(locale?: string): string {
  if (locale && Object.hasOwn(WebsiteLocaleMapping, locale)) {
    return WebsiteLocaleMapping[locale as AppLocale];
  }
  return WebsiteLocaleMapping[DEFAULT_LOCALE];
}

function resolveVoiceFromOptions(options: MediaStreamingUrlOptions): string {
  if (options.voice) return options.voice;
  if (options.coachSound !== undefined || options.coachStyle !== undefined) {
    return coachToMaiaFallbackVoiceFolder(
      resolveCoachFromPreferences(options.coachSound, options.coachStyle),
    );
  }
  return DEFAULT_MAIA_VOICE;
}

function hasMaiaFallbackOverrides(options: MediaStreamingUrlOptions, originalUrl: string): boolean {
  if (originalUrl.includes(MAIA_VOICE_PLACEHOLDER)) return true;
  if (options.locale) return true;
  if (options.voice) return true;
  if (options.coachSound !== undefined || options.coachStyle !== undefined) return true;
  return false;
}

/**
 * Swaps the default distribution host for the environment's one across a whole
 * block of text (CMS markdown pins the default distribution at export time, and
 * a document can reference it dozens of times). Left untouched when either host
 * is unset, so a misconfigured build degrades to the authored URLs rather than
 * rewriting them to the string "undefined".
 */
export const resolveMediaStreamingHost = (text?: string | null): string => {
  if (!text) return "";
  if (!DEFAULT_MEDIA_STREAMING_DISTRIBUTION_URL || !MEDIA_STREAMING_DISTRIBUTION_URL) return text;
  return text.replaceAll(DEFAULT_MEDIA_STREAMING_DISTRIBUTION_URL, MEDIA_STREAMING_DISTRIBUTION_URL);
};

export const getMediaStreamingUrl = (
  url?: string | null | undefined,
  optionsOrVoice?: string | MediaStreamingUrlOptions,
): string => {
  if (!url) return "";

  const options = normalizeOptions(optionsOrVoice);
  const voice = resolveVoiceFromOptions(options);
  const language = options.locale ? resolveMaiaFallbackLanguage(options.locale) : undefined;
  const shouldRewriteFallback = hasMaiaFallbackOverrides(options, url);

  let result = url.replace(DEFAULT_MEDIA_STREAMING_DISTRIBUTION_URL, MEDIA_STREAMING_DISTRIBUTION_URL);

  result = result.replaceAll(MAIA_VOICE_PLACEHOLDER, voice);
  if (language && result.includes(MAIA_LANG_PLACEHOLDER)) {
    result = result.replaceAll(MAIA_LANG_PLACEHOLDER, language);
  }

  if (shouldRewriteFallback && MAIA_ECHO_FALLBACKS_PATH.test(result)) {
    result = result.replace(MAIA_ECHO_FALLBACKS_PATH, (_match, prefix, currentLang, slash, _voice, suffix) => {
      const resolvedLang = language ?? currentLang;
      return `${prefix}${resolvedLang}${slash}${voice}${suffix}`;
    });
  }

  return result;
};
